/**
 * Item-array metadata for section families with repeatable items.
 *
 * Consumed by the portal's ItemListEditor to enforce min/max bounds,
 * generate defaults for new items, and display summary labels.
 */

export interface ItemArrayMeta {
  /** Property name on the section props that holds the items array. */
  prop: string;
  /** Minimum number of items allowed. */
  minItems: number;
  /** Maximum number of items allowed. */
  maxItems: number;
  /** Singular noun used in UI labels ("feature", "testimonial", etc.). */
  itemLabel: string;
  /** Item prop whose value is shown as the collapsed summary label. */
  summaryProp: string;
  /** Default values for a newly added item. */
  itemDefaults: Record<string, unknown>;
}

export interface ColumnMeta {
  /** Property name on the section props that holds the column headers array. */
  prop: string;
  /** Minimum number of columns allowed. */
  minColumns: number;
  /** Maximum number of columns allowed. */
  maxColumns: number;
  /** Default header text for a new column. */
  defaultHeader: string;
}

export interface SectionItemMeta {
  items: ItemArrayMeta;
  columns?: ColumnMeta;
}

export const SECTION_ITEM_META: Record<string, SectionItemMeta> = {
  FeatureGrid: {
    items: {
      prop: "items",
      minItems: 1,
      maxItems: 12,
      itemLabel: "feature",
      summaryProp: "title",
      itemDefaults: { icon: "", title: "Feature", body: "Description of this feature." },
    },
  },
  FeatureList: {
    items: {
      prop: "items",
      minItems: 1,
      maxItems: 12,
      itemLabel: "feature",
      summaryProp: "title",
      itemDefaults: { icon: "", title: "Feature", body: "Description of this feature." },
    },
  },
  Testimonials: {
    items: {
      prop: "items",
      minItems: 1,
      maxItems: 8,
      itemLabel: "testimonial",
      summaryProp: "quote",
      itemDefaults: { quote: "Great experience!", author: "Happy Client" },
    },
  },
  TeamGrid: {
    items: {
      prop: "members",
      minItems: 1,
      maxItems: 16,
      itemLabel: "team member",
      summaryProp: "name",
      itemDefaults: { name: "Team Member", title: "Professional" },
    },
  },
  StatsBar: {
    items: {
      prop: "stats",
      minItems: 1,
      maxItems: 6,
      itemLabel: "stat",
      summaryProp: "value",
      itemDefaults: { value: "0", label: "Statistic" },
    },
  },
  ProcessSteps: {
    items: {
      prop: "steps",
      minItems: 2,
      maxItems: 8,
      itemLabel: "step",
      summaryProp: "title",
      itemDefaults: { title: "Step", body: "Description of this step." },
    },
  },
  LogoStrip: {
    items: {
      prop: "logos",
      minItems: 2,
      maxItems: 20,
      itemLabel: "logo",
      summaryProp: "alt",
      itemDefaults: { alt: "Partner logo" },
    },
  },
  CTACards: {
    items: {
      prop: "cards",
      minItems: 1,
      maxItems: 6,
      itemLabel: "card",
      summaryProp: "heading",
      itemDefaults: { heading: "Card Title", body: "A brief description of this option.", ctaLabel: "Learn More", ctaHref: "#" },
    },
  },
  FeatureShowcase: {
    items: {
      prop: "items",
      minItems: 1,
      maxItems: 6,
      itemLabel: "feature",
      summaryProp: "title",
      itemDefaults: { title: "Service Title", body: "Description of this service or capability." },
    },
  },
  LeadForm: {
    items: {
      prop: "fields",
      minItems: 0,
      maxItems: 8,
      itemLabel: "question",
      summaryProp: "label",
      itemDefaults: { label: "Question", type: "text", required: false, options: "" },
    },
  },
  ComparisonTable: {
    items: {
      prop: "rows",
      minItems: 1,
      maxItems: 20,
      itemLabel: "row",
      summaryProp: "feature",
      itemDefaults: { feature: "Feature", values: [true, true] },
    },
    columns: {
      prop: "columnHeaders",
      minColumns: 2,
      maxColumns: 4,
      defaultHeader: "New Plan",
    },
  },
};
